import React, { useState } from 'react';
import Button from './ui/Button';
import Icon from './AppIcon';
import ImportWizard from '../pages/client-management/components/ImportWizard';
import { importClientsFromCSV } from '../utils/clientService';

/**
 * Onboarding step: import contacts using the same ImportWizard as Client Management.
 */
export default function ContactsImportOnboarding({ onComplete }) {
  const [wizardOpen, setWizardOpen] = useState(false);
  const [error, setError] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  const handleImport = async (importData) => {
    setError(null);
    try {
      if (importData?.__pastClientsResults) {
        const results = importData.__pastClientsResults;
        setLastResult(results);
        setWizardOpen(false);
        if (results.successful > 0 || results.updated > 0) {
          onComplete?.({ imported: true, results });
        }
        return;
      }

      const { data: results, error: importError } = await importClientsFromCSV(importData);
      if (importError) {
        setError(importError.message);
        throw new Error(importError.message);
      }
      setLastResult(results);
      setWizardOpen(false);
      if (results.successful > 0 || results.updated > 0) {
        onComplete?.({ imported: true, results });
      }
    } catch (err) {
      console.error('Onboarding import error:', err);
      setError(err.message || 'Import failed');
    }
  };

  return (
    <div className="max-w-md mx-auto text-center p-6">
      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
        <Icon name="Users" size={32} className="text-primary" />
      </div>

      <h2 className="text-2xl font-bold text-foreground mb-2">Import your contacts</h2>
      <p className="text-muted-foreground mb-6">
        Add past clients so they can get your monthly messages. Same import tools you&apos;ll use later
        in Client Management.
      </p>

      {lastResult && (lastResult.successful > 0 || lastResult.updated > 0) && (
        <div className="mb-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">
          Imported {lastResult.successful || 0}
          {lastResult.updated ? `, updated ${lastResult.updated}` : ''}.
        </div>
      )}

      {lastResult?.missingPhone > 0 && (
        <div className="mb-4 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
          {lastResult.missingPhone} contact{lastResult.missingPhone === 1 ? '' : 's'} imported without a phone
          number — they won&apos;t receive texts until a phone is added in Client Management.
        </div>
      )}

      {error && (
        <div className="mb-4 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg p-3">
          {error}
        </div>
      )}

      <div className="space-y-3">
        <Button onClick={() => setWizardOpen(true)} className="w-full">
          <Icon name="Upload" size={16} className="mr-2" />
          Import contacts
        </Button>
        <Button
          variant="outline"
          onClick={() => onComplete?.({ imported: false, skipped: true })}
          className="w-full"
        >
          {lastResult && (lastResult.successful > 0 || lastResult.updated > 0)
            ? 'Continue'
            : 'Skip for now'}
        </Button>
        <p className="text-xs text-muted-foreground">
          Skipping isn&apos;t recommended — you can import anytime from Client Management.
        </p>
      </div>

      <ImportWizard
        isOpen={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onImport={handleImport}
      />
    </div>
  );
}
